<script lang="ts">
    import { createEventDispatcher } from 'svelte';
    import type { Writable } from 'svelte/store';

    import SearchInput from '@amp/web-app-components/src/components/SearchInput/SearchInput.svelte';
    import { searchItem } from '~/constants/navigation-items';
    import { getJet } from '~/jet';
    import { getI18n } from '~/stores/i18n';
    import { getLocale, Locale } from '~/utils/locale';
    import type { NavigationItem } from '@amp/web-app-components/src/components/Navigation/types';
    import {
        type FlowAction,
        makeFlowAction,
    } from '@jet-app/music-classical/models/actions';
    import { makeSearchPageIntent } from '@jet-app/music-classical/intents/search/SearchPageIntent';
    import TopSearchListResult from '~/components/jet/lockup/TopSearchListResult.svelte';
    import SearchSuggestions from '@amp/web-app-components/src/components/SearchSuggestions/SearchSuggestions.svelte';
    import type { SearchSuggestionLockup } from '@jet-app/music-classical/models/pages/search';

    const dispatch = createEventDispatcher();
    const jet = getJet();
    const i18n = getI18n();
    const locale = getLocale();

    /**
     * The store containing the currently selected tab.
     */
    export let currentTab: Writable<any | null>;
    export let defaultValue: string | null = null;

    function makeSearchQueryFromInput(
        event: CustomEvent<{ term: string }>,
    ): void {
        const { term } = event.detail;
        performSearch(term);
    }

    function performSearch(term: string): void {
        const searchAction = makeFlowAction(
            makeSearchPageIntent({
                ...Locale.canonicalize(locale),
                term,
            }),
            {
                data: [],
                custom: {},
            },
        );

        //73165545 (Error Handling Across App)
        jet.perform(searchAction);
    }

    function makeSearchQueryFromSuggestion(
        event: CustomEvent<{ suggestion: SearchSuggestionLockup }>,
    ): void {
        const eventAction = event?.detail?.suggestion?.segue;

        if (eventAction) {
            jet.perform(eventAction);
        } else {
            performSearch(event.detail.suggestion.displayTerm);
        }
    }

    async function getSuggestionsForPartialTerm(
        partialTerm: string,
    ): Promise<any[]> {
        const searchSuggestionIntent = makeSearchPageIntent({
            ...Locale.canonicalize(locale),
            term: partialTerm,
            isSearchSuggestions: true,
        });

        logMetricsEventForPartialSearch(partialTerm);
        const page = (await jet.dispatch(
            searchSuggestionIntent,
        )) as unknown as SearchSuggestions;

        return page.items;
    }

    async function logMetricsEventForPartialSearch(
        partialTerm: string,
    ): Promise<void> {
        jet.recordCustomMetricsEvent({
            eventType: 'search',
            targetType: 'HintListItem',
            actionType: 'hint',
            pageId: 'SearchHints',
            pageContext: 'Search',
            term: partialTerm,
            actionDetails: {
                searchPrefix: partialTerm,
            },
        });
    }

    function makeLandingFlowAction(): FlowAction {
        return makeFlowAction(
            makeSearchPageIntent({
                ...Locale.canonicalize(locale),
            }),
            {
                data: [],
                custom: {},
            },
        );
    }

    function onMenuItemClick(event: CustomEvent) {
        const menuItem = event.detail as NavigationItem;
        const from =
            typeof menuItem?.opaqueData === 'function'
                ? menuItem.opaqueData(locale).from
                : null;

        if (from === 'searchInputClear') {
            const flowAction = makeLandingFlowAction();
            // Override Menu Item Opaque Data with custom Flow Action with required Metrics Data
            menuItem.opaqueData = () => flowAction;
        }

        dispatch('menuItemClick', menuItem);
    }

    function onClear() {
        dispatch('clear', makeLandingFlowAction());
    }

    function onClickedOutside() {
        dispatch('clickedOutside');
    }

    function onSearchInputHasFocus() {
        dispatch('searchInputHasFocus');
    }

    function onShowSearchSuggestions(e: CustomEvent) {
        dispatch('showSearchSuggestions', e);
    }
</script>

<div class="search-input-wrapper" data-testid="search-input">
    <SearchInput
        on:makeSearchQueryFromInput={makeSearchQueryFromInput}
        on:makeSearchQueryFromSuggestion={makeSearchQueryFromSuggestion}
        on:menuItemClick={onMenuItemClick}
        on:searchInputHasFocus={onSearchInputHasFocus}
        on:clickedOutside={onClickedOutside}
        on:showSearchSuggestions={onShowSearchSuggestions}
        on:clear={onClear}
        {defaultValue}
        {currentTab}
        menuItem={searchItem}
        translateFn={$i18n.t}
        {getSuggestionsForPartialTerm}
    >
        <svelte:fragment slot="searchScopeBar">
            <slot name="searchScopeBar" />
        </svelte:fragment>
        <svelte:fragment slot="suggestion" let:suggestion>
            <TopSearchListResult item={suggestion} />
        </svelte:fragment>
    </SearchInput>
</div>

<style lang="scss">
    .search-input-wrapper {
        --searchBoxIconFill: var(--keyColor);
        position: relative;
        z-index: var(--z-default);

        // Temp styles that can be cleaned up or removed as part of:
        // rdar://89046322 (JMOTW | Search page: support for mobile viewports)
        margin-inline-start: var(--bodyGutter);
        margin-inline-end: var(--bodyGutter);

        @media (--range-sidebar-hidden-down) {
            margin-top: 24px;
        }

        @media (--sidebar-visible) {
            display: flex;
            flex-direction: column;
            justify-content: center;
            margin-inline-start: 25px;
            margin-inline-end: 25px;
        }

        :global(.suggestion) {
            // Allow complex search suggestions to wrap to 2 lines
            --searchSuggestionClampedLines: 2;
            word-break: break-word;
        }

        :global(.search-suggestions) {
            @media (--sidebar-visible) {
                width: 320px;
                padding: 7px 0;
            }
        }

        :global(.search-hint) {
            border-radius: 4px;
        }
    }
</style>
